import { useCallback, useEffect, useState } from 'react';

import Constants from 'expo-constants';
import * as ExpoScreenOrientation from 'expo-screen-orientation';

import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import { ScreenOrientation } from './types';

interface UseScreenOrientationParams {
  onOrientationChange: (orientation: ScreenOrientation) => void;
}

interface UseScreenOrientationReturnType {
  orientation: ScreenOrientation;
  setOrientation: (newOrientation: ScreenOrientation) => Promise<void>;
  setOrientationUnlocked: () => Promise<void>;
}

// Using our default orientation configured in app.config.ts
const defaultOrientation =
  Constants.expoConfig?.orientation === 'portrait'
    ? ScreenOrientation.PORTRAIT
    : ScreenOrientation.LANDSCAPE;

/**
 * Hook for managing screen orientation and detecting orientation changes.
 *
 * This hook provides the following features:
 *
 * - Tracks the current screen orientation (either 'PORTRAIT' or 'LANDSCAPE').
 * - Provides functions to lock the orientation to a specific mode or unlock it to automatically change the screen orientation
 * - Executes the `onOrientationChange` callback whenever the orientation changes.
 * - Resets the orientation to the default value on unmount.
 *
 * @param onOrientationChange Callback function to be executed whenever the screen orientation changes.
 * @returns An object containing the following:
 *   - `orientation`: The current screen orientation ('PORTRAIT' or 'LANDSCAPE').
 *   - `setOrientation`: A function to lock the screen orientation to a specific mode.
 *   - `setOrientationUnlocked`: A function to unlock the screen orientation, allowing it to change freely when the device rotates.
 */
export const useScreenOrientation = ({
  onOrientationChange,
}: UseScreenOrientationParams): UseScreenOrientationReturnType => {
  const [orientation, setOrientation] = useState<ScreenOrientation>(defaultOrientation);

  const setOrientationLock = useCallback(async (newOrientation: ScreenOrientation) => {
    try {
      await ExpoScreenOrientation.lockAsync(
        newOrientation === ScreenOrientation.LANDSCAPE
          ? ExpoScreenOrientation.OrientationLock.LANDSCAPE
          : ExpoScreenOrientation.OrientationLock.PORTRAIT_UP
      );
    } catch (e) {
      /*
       * If the screen orientation lock fails, it might be due to the web app
       * not having full support for screen orientation on certain web OS.
       * We could potentially improve this in the future to enable screen orientation
       * locking across a wider range of web platforms.
       * https://developer.mozilla.org/en-US/docs/Web/API/ScreenOrientation/lock#browser_compatibility
       */
      await ExpoScreenOrientation.lockAsync(ExpoScreenOrientation.OrientationLock.PORTRAIT_UP);
    }

    setOrientation(newOrientation);
  }, []);

  const setOrientationUnlocked = useCallback(async () => ExpoScreenOrientation.unlockAsync(), []);

  /**
   * Handles screen orientation changes.
   *
   * - Detects orientation changes (lockOrientation or unlockOrientation).
   * - Calls `onOrientationChange` and updates internal state.
   * - Stores initial orientation for later comparison.
   *
   */
  const handleOrientationChange = useCallback(async () => {
    const expoOrientation = await ExpoScreenOrientation.getOrientationAsync();
    const newOrientation =
      expoOrientation === ExpoScreenOrientation.Orientation.LANDSCAPE_LEFT ||
      expoOrientation === ExpoScreenOrientation.Orientation.LANDSCAPE_RIGHT
        ? ScreenOrientation.LANDSCAPE
        : ScreenOrientation.PORTRAIT;

    onOrientationChange(newOrientation);
    setOrientation(newOrientation);
  }, [onOrientationChange]);

  // Listen to orientation changes
  useEffect(() => {
    const subscription = ExpoScreenOrientation.addOrientationChangeListener(
      handleOrientationChange
    );

    return () => {
      ExpoScreenOrientation.removeOrientationChangeListener(subscription);
      ExpoScreenOrientation.lockAsync(ExpoScreenOrientation.OrientationLock.PORTRAIT_UP); //the app is locked by default
    };
  }, [handleOrientationChange, setOrientationLock]);

  /*
   *  - By persisting this flag in LocalStorage, we can track if the user has changed the orientation from the default.
   *  - In the App container, we can check this flag to decide whether to reset the orientation to the default.
   *  - This avoids unnecessary calls to ExpoScreenOrientation.getOrientationAsync(), which can trigger permission prompts for users who haven't used this feature yet.
   *  - https://github.com/rbilabs/bk-us-app/blob/4473003c8c8263199e51920ca819c29ea12439a3/src/components/app-container/index.tsx#L74
   * */
  useEffect(() => {
    syncStorage.setBoolean(
      SyncStorageKey.IS_ORIENTATION_DEFAULT,
      orientation === defaultOrientation
    );
  }, [orientation]);

  return {
    orientation,
    setOrientation: setOrientationLock,
    setOrientationUnlocked,
  };
};
